import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';

import {
  adoptBuilt,
  castProblem,
  editRole,
  lessonState,
  MODULE_CAST_CAP,
  oneOff,
  READINESS_LABEL,
  removeAssignment,
  setLead,
  toggleInLesson,
  withCharacter,
} from './cast';

import type { Adoption, CastDraft } from './cast';
import type { CastEntry, CastId } from '@platform/schema/cast';
import type { LessonCast } from '@platform/schema/content';
import type { CastAssignment } from '@platform/schema/curriculum';

export interface SavedCast {
  draft: CastDraft;
  revision: number;
}

/** What the story map shows of a lesson, beside its subset. */
export interface MapLesson {
  lessonId: string;
  name: string;
  description: string;
  storyBeat: string;
  storyChanged: boolean;
  built: LessonCast;
}

const svgSource = (svg: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

function Portrait({ entry }: { entry: CastEntry | undefined }) {
  return (
    <Avatar
      alt=""
      src={entry?.portrait == null ? undefined : svgSource(entry.portrait)}
    >
      {entry?.name.charAt(0) ?? '?'}
    </Avatar>
  );
}

export const assignmentName = (
  one: CastAssignment,
  people: ReadonlyMap<CastId, CastEntry>,
): string =>
  one.character === undefined
    ? (one.name ?? '')
    : (people.get(one.character)?.name ?? one.character);

export const castLine = (
  one: CastAssignment,
  people: ReadonlyMap<CastId, CastEntry>,
): string =>
  `${assignmentName(one, people)} — ${one.role}${one.part === 'lead' ? ' (lead)' : ''}`;

/**
 * A module's cast and its lessons' subsets — CURR-43. Saved through the story revision like
 * `StoryEditor`, with the same discipline: the revision is the one the first edit started
 * from, a refused save keeps every choice, and Reload shows the saved cast before anything
 * replaces the draft.
 */
export function ModuleCast({
  people,
  onReplace,
  lessons,
  saved,
  save,
  load,
}: {
  people: readonly CastEntry[];
  /** Replace a saved assignment's character in every built lesson — CURR-48. */
  onReplace: (assignment: CastAssignment) => void;
  lessons: readonly MapLesson[];
  saved: SavedCast;
  save: (draft: CastDraft, revision: number) => Promise<unknown>;
  load: () => Promise<SavedCast>;
}) {
  const [draft, setDraft] = useState<CastDraft | null>(null);
  const [base, setBase] = useState<number | null>(null);
  const [latest, setLatest] = useState<SavedCast | null>(null);
  const [adoption, setAdoption] = useState<Adoption | null>(null);
  const [search, setSearch] = useState('');
  const [oneOffName, setOneOffName] = useState('');
  const current = draft ?? saved.draft;
  const dirty =
    draft !== null && JSON.stringify(draft) !== JSON.stringify(saved.draft);
  const problem = castProblem(current.cast);
  const byId = new Map(people.map((one) => [one.id, one]));
  const full = current.cast.length >= MODULE_CAST_CAP;

  const edit = (next: CastDraft): void => {
    setBase((was) => was ?? saved.revision);
    setDraft(next);
  };
  const discard = (): void => {
    setDraft(null);
    setBase(null);
    setLatest(null);
    setAdoption(null);
  };

  const write = useTrackedMutation('Save module cast', {
    mutationFn: () => save(current, base ?? saved.revision),
    onSuccess: discard,
  });
  const reload = useTrackedMutation('Reload module cast', {
    mutationFn: load,
    onSuccess: (cast) => {
      setLatest(cast);
      setBase(cast.revision);
      write.reset();
    },
  });

  const cast = new Set(current.cast.flatMap((one) => one.character ?? []));
  const needle = search.trim().toLowerCase();
  const shown = people.filter(
    (one) =>
      needle === '' ||
      one.name.toLowerCase().includes(needle) ||
      one.personality.toLowerCase().includes(needle),
  );
  const builtLessons = lessons.filter((one) => one.built.length > 0);

  return (
    <Stack
      component="form"
      aria-label="Story & characters"
      spacing={2}
      onSubmit={(event) => {
        event.preventDefault();
        write.mutate();
      }}
    >
      <Typography variant="h6" component="h3">
        Story &amp; characters
      </Typography>

      {current.cast.length === 0 && (
        <Stack spacing={0.5}>
          <Typography>Choose who this story follows.</Typography>
          <Typography variant="body2" color="text.secondary">
            Keep each character’s role consistent throughout this module.
          </Typography>
        </Stack>
      )}

      {current.cast.length === 0 && builtLessons.length > 0 && (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const adopted = adoptBuilt(lessons);
                setAdoption(adopted);
                edit({
                  cast: adopted.draft.cast,
                  subsets: { ...current.subsets, ...adopted.draft.subsets },
                });
              }}
            >
              Adopt their cast
            </Button>
          }
        >
          {builtLessons.length === 1
            ? 'A built lesson in this module already has a cast.'
            : `${String(builtLessons.length)} built lessons in this module already have a cast.`}{' '}
          Adopt it as the module cast?
        </Alert>
      )}

      {adoption !== null &&
        (adoption.conflicts.length > 0 || adoption.left.length > 0) && (
          <Alert severity="warning" aria-label="Cast conflicts">
            <Stack spacing={1}>
              {adoption.conflicts.map(({ id, roles }) => (
                <Stack
                  key={id}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <Typography variant="body2">
                    {byId.get(id)?.name ?? id} is cast as {roles.join(' and ')}.
                    A character keeps one role in a module.
                  </Typography>
                  {roles.map((role) => (
                    <Button
                      key={role}
                      size="small"
                      color="inherit"
                      disabled={
                        current.cast.find((one) => one.id === id)?.role === role
                      }
                      onClick={() => {
                        edit(editRole(current, id, role));
                      }}
                    >
                      Use {role}
                    </Button>
                  ))}
                </Stack>
              ))}
              {adoption.left.length > 0 && (
                <Typography variant="body2">
                  Not adopted, past the limit of {MODULE_CAST_CAP}:{' '}
                  {adoption.left
                    .map((id) => byId.get(id)?.name ?? id)
                    .join(', ')}
                  .
                </Typography>
              )}
            </Stack>
          </Alert>
        )}

      {current.cast.length > 0 && (
        <Stack spacing={1} component="ul" sx={{ m: 0, p: 0 }}>
          {current.cast.map((one) => {
            const name = assignmentName(one, byId);
            const entry =
              one.character === undefined ? undefined : byId.get(one.character);
            return (
              <Stack
                key={one.id}
                component="li"
                direction="row"
                spacing={1}
                aria-label={name}
                sx={{ alignItems: 'center', listStyle: 'none' }}
              >
                <Portrait entry={entry} />
                <Stack sx={{ minWidth: '8rem' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {entry === undefined
                      ? 'One-off'
                      : READINESS_LABEL[entry.readiness]}
                  </Typography>
                </Stack>
                {one.part === 'lead' && (
                  <Chip size="small" color="primary" label="Lead" />
                )}
                <TextField
                  size="small"
                  label={`${name}’s role`}
                  value={one.role}
                  onChange={(event) => {
                    edit(editRole(current, one.id, event.target.value));
                  }}
                  sx={{ flex: 1 }}
                />
                {one.part !== 'lead' && (
                  <Button
                    size="small"
                    onClick={() => {
                      edit(setLead(current, one.id));
                    }}
                  >
                    Set as lead
                  </Button>
                )}
                {one.character !== undefined && (
                  <Button
                    size="small"
                    disabled={dirty}
                    onClick={() => {
                      onReplace(one);
                    }}
                  >
                    Replace character
                  </Button>
                )}
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    edit(removeAssignment(current, one.id));
                  }}
                >
                  Remove
                </Button>
              </Stack>
            );
          })}
        </Stack>
      )}

      <TextField
        size="small"
        type="search"
        label="Search characters"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
      />
      <Stack
        role="list"
        aria-label="Characters"
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        {shown.map((one) => (
          <Card
            key={one.id}
            role="listitem"
            variant="outlined"
            sx={{ flex: '1 1 14rem' }}
          >
            <CardActionArea
              aria-label={`Add ${one.name}`}
              disabled={cast.has(one.id) || full}
              onClick={() => {
                edit(withCharacter(current, one.id));
              }}
              sx={{ p: 1, height: '100%' }}
            >
              <Stack direction="row" spacing={1}>
                <Portrait entry={one} />
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {one.name}
                    {one.age === null ? '' : `, ${String(one.age)}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {one.personality}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      cast.has(one.id)
                        ? 'In this module'
                        : READINESS_LABEL[one.readiness]
                    }
                    sx={{ alignSelf: 'flex-start' }}
                  />
                </Stack>
              </Stack>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
      {shown.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No character matches “{search.trim()}”.
        </Typography>
      )}

      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TextField
          size="small"
          label="One-off character"
          helperText="One-off mode: someone who appears only in this module, drawn as a generic figure."
          value={oneOffName}
          onChange={(event) => {
            setOneOffName(event.target.value);
          }}
          sx={{ flex: 1 }}
        />
        <Button
          disabled={oneOffName.trim() === '' || full}
          onClick={() => {
            edit(oneOff(current, oneOffName.trim()));
            setOneOffName('');
          }}
        >
          Add one-off
        </Button>
      </Stack>

      <Typography variant="subtitle2" component="h4">
        Lesson story map
      </Typography>
      <Table size="small" aria-label="Lesson story map">
        <TableHead>
          <TableRow>
            <TableCell>Lesson</TableCell>
            <TableCell>Objective</TableCell>
            <TableCell>Beat</TableCell>
            <TableCell>Cast</TableCell>
            <TableCell>State</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lessons.map((lesson) => {
            const subset = current.subsets[lesson.lessonId] ?? [];
            return (
              <TableRow key={lesson.lessonId} aria-label={lesson.lessonId}>
                <TableCell>
                  <Typography variant="body2">{lesson.lessonId}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lesson.name}
                  </Typography>
                </TableCell>
                <TableCell>{lesson.description}</TableCell>
                <TableCell>{lesson.storyBeat}</TableCell>
                <TableCell>
                  {current.cast.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No module cast
                    </Typography>
                  ) : (
                    current.cast.map((one) => (
                      <FormControlLabel
                        key={one.id}
                        label={assignmentName(one, byId)}
                        control={
                          <Checkbox
                            size="small"
                            checked={subset.includes(one.id)}
                            onChange={() => {
                              edit(
                                toggleInLesson(
                                  current,
                                  lesson.lessonId,
                                  one.id,
                                ),
                              );
                            }}
                          />
                        }
                      />
                    ))
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={lessonState(lesson, subset)} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {problem !== null && current.cast.length > 0 && (
        <Typography variant="body2" color="error">
          {problem}
        </Typography>
      )}
      {write.isError && (
        <Alert
          severity="error"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                disabled={write.isPending}
                onClick={() => {
                  write.mutate();
                }}
              >
                Retry
              </Button>
              <Button
                color="inherit"
                size="small"
                disabled={reload.isPending}
                onClick={() => {
                  reload.mutate();
                }}
              >
                Reload
              </Button>
            </Stack>
          }
        >
          {describeApiError(write.error)} Your choices are still here.
        </Alert>
      )}
      {reload.isError && (
        <Alert severity="error">{describeApiError(reload.error)}</Alert>
      )}
      {latest !== null && (
        <Alert
          severity="info"
          aria-label="Saved cast"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setDraft(latest.draft);
                setLatest(null);
              }}
            >
              Use saved cast
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Saved cast, revision {latest.revision}. Saving now replaces it with
            your choices.
          </Typography>
          <Typography variant="body2">
            {latest.draft.cast.length === 0
              ? '(nobody)'
              : latest.draft.cast.map((one) => castLine(one, byId)).join(', ')}
          </Typography>
        </Alert>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={!dirty || write.isPending || problem !== null}
        >
          Save cast
        </Button>
        <Button
          size="small"
          disabled={draft === null || write.isPending}
          onClick={() => {
            discard();
            write.reset();
          }}
        >
          Undo changes
        </Button>
      </Stack>
    </Stack>
  );
}
